import { eventChannel, END } from 'redux-saga';
import { all, call, take, put, fork, cancel, takeEvery } from 'redux-saga/effects';
import siteConfig from '@iso/config/site.config';
import authActions from '@iso/redux/auth/actions';
import inventarioActions from '@iso/redux/inventario/actions';
import productosActions from '@iso/redux/productos/actions';
import socketActions from './actions';

// Derivar la URL de WebSocket a partir de la URL de la API
const getWsBase = () =>
    siteConfig.siteUrl.API_URL
        .replace('https://', 'wss://')
        .replace('http://', 'ws://')
        .replace(/\/$/, '');

// Crea un eventChannel que envuelve el ciclo de vida del WebSocket
function createSocketChannel(token, authId) {
    return eventChannel(emit => {
        const base   = getWsBase();
        const params = new URLSearchParams({ token, authId: authId || '' });
        const ws     = new WebSocket(`${base}?${params.toString()}`);

        ws.onopen    = () => emit({ type: 'OPEN' });
        ws.onmessage = (ev) => {
            try {
                const data = JSON.parse(ev.data);
                emit({ type: 'MESSAGE', data });
            } catch (_) { /* mensaje mal formateado, ignorar */ }
        };
        ws.onerror   = () => emit(END);
        ws.onclose   = () => emit(END);

        // Cleanup: cerrar el socket si el canal se cancela
        return () => {
            if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
                ws.close();
            }
        };
    });
}

// Despachar las acciones Redux correspondientes según el módulo/acción del evento
function* handleSocketEvent({ module: mod, action }) {
    if (mod === 'inventario') {
        // Refrescar stock y alertas automáticamente para todos los que tengan
        // el reducer Inventario activo (StockActual, alertas en la barra, etc.)
        yield put({ type: inventarioActions.GET_STOCK_REQUEST });
        yield put({ type: inventarioActions.GET_ALERTAS_REQUEST });
    }
    if (mod === 'productos') {
        // create/update/delete(archivar)/restore de un producto: refrescar el
        // listado global para que selectores en Pedidos, Cotizaciones,
        // Opcionales, Facturación e Informes dejen de mostrar productos
        // archivados en sesiones que ya tenían el listado cargado.
        yield put({ type: productosActions.GET_PRODUCTOS });
    }
    // Agregar aquí otros módulos si se necesitan en el futuro:
    // if (mod === 'ventas') { yield put(...) }
}

// Worker: conecta el socket y escucha eventos hasta que el canal cierre
function* socketWorker() {
    const token  = localStorage.getItem('access_token');
    const authId = localStorage.getItem('id_token');

    if (!token) return;

    yield put(socketActions.connecting());

    const channel = yield call(createSocketChannel, token, authId);

    try {
        while (true) {
            const event = yield take(channel);
            if (event.type === 'OPEN') {
                yield put(socketActions.connected());
            } else if (event.type === 'MESSAGE') {
                yield call(handleSocketEvent, event.data);
            }
        }
    } finally {
        yield put(socketActions.disconnected());
    }
}

// Supervisor: conecta en LOGIN_SUCCESS, desconecta en LOGOUT
function* watchAuth() {
    let task = null;
    while (true) {
        yield take(authActions.LOGIN_SUCCESS);

        // Cancelar la conexión previa si existe (ej. recarga de página)
        if (task) yield cancel(task);
        task = yield fork(socketWorker);

        yield take(authActions.LOGOUT);
        if (task) {
            yield cancel(task);
            task = null;
        }
        yield put(socketActions.disconnected());
    }
}

export default function* socketSaga() {
    yield all([call(watchAuth)]);
}
